/**
 * The words that say when a reading is (InOut history, 22 Oct 2026 — H7): a
 * running InOut is read today and says "today", "from today", "you are here";
 * a closed InOut is read as of the day it was closed, and every one of those
 * phrases says that day instead ("on 22 Oct 2026", "from 22 Oct 2026",
 * "where you were on 22 Oct 2026") — once days pass, "today" would be false.
 *
 * `pastDay` (`InOutPastDay`): null — today, the live words, unchanged; a day
 * in words — the day the reading is as of. One function per phrase a reading
 * says, each with literal keys, for the web's limits and band; the answer's
 * own line is `inoutAnswerText`'s and a rule's figures `inoutClockFiguresText`'s.
 */
import type { TFunction } from 'i18next';

import {
  inoutClockFiguresText,
  type InOutCountedClock,
  type InOutLimitCounter,
  type InOutLimitTrips,
  type InOutPastDay,
} from './presentation';

type FormatNumber = (value: number) => string;

/** The marker on a limit's line: "today", or that the InOut was closed there. */
export function inoutLineMarkerText(t: TFunction, pastDay: InOutPastDay): string {
  return pastDay === null ? t('inout.limits.line.today') : t('inout.limits.line.closed');
}

/** No rule of the title applies — today, or on the day read. */
export function inoutNoRuleText(t: TFunction, pastDay: InOutPastDay): string {
  return pastDay === null ? t('inout.clocks.empty') : t('inout.clocks.emptyOn', { date: pastDay });
}

/** No title in force — today, or on the day read. */
export function inoutNoTitleText(t: TFunction, pastDay: InOutPastDay): string {
  return pastDay === null
    ? t('inout.banner.noTitle')
    : t('inout.banner.noTitleOn', { date: pastDay });
}

/** Days still to confirm on a rule — the person's to settle today; on a past day, not confirmed by then. */
export function inoutMarginText(
  margin: number,
  { t, formatNumber, pastDay }: { t: TFunction; formatNumber: FormatNumber; pastDay: InOutPastDay }
): string {
  const said = { count: margin, days: formatNumber(margin) };
  return pastDay === null
    ? t('inout.clock.margin', said)
    : t('inout.clock.marginOn', { ...said, date: pastDay });
}

/** When the limit would be passed at the pace counted — today's pace, or the pace on the day read. */
export function inoutExhaustsText(
  exhaustsOn: string,
  { t, pastDay }: { t: TFunction; pastDay: InOutPastDay }
): string {
  return pastDay === null
    ? t('inout.clock.exhaustsOn', { date: exhaustsOn })
    : t('inout.clock.exhaustsOnFrom', { date: exhaustsOn, asOf: pastDay });
}

/** In the band two norms read apart — now, or on the day read. */
export function inoutInRiskZoneText(t: TFunction, pastDay: InOutPastDay): string {
  return pastDay === null
    ? t('inout.clock.inRiskZone')
    : t('inout.clock.inRiskZoneOn', { date: pastDay });
}

/** The 90/180's figures — "Today: …", or "On 22 Oct 2026: …". */
export function inoutShortStayFiguresText(
  clock: InOutCountedClock,
  { t, formatNumber, pastDay }: { t: TFunction; formatNumber: FormatNumber; pastDay: InOutPastDay }
): string {
  const figures = inoutClockFiguresText(clock, t, formatNumber, pastDay);
  return pastDay === null
    ? t('inout.shortStay.today', { figures })
    : t('inout.shortStay.on', { figures, date: pastDay });
}

/** The row of the 90/180's table where the person is — or was, on the day read. */
export function inoutWhereMarkText(t: TFunction, pastDay: InOutPastDay): string {
  return pastDay === null
    ? t('inout.shortStay.youAreHere')
    : t('inout.shortStay.youWereHere', { date: pastDay });
}

/**
 * Why one trip can last what the answer says (`inoutAnswerWhy`), in words:
 * the heading, and the line that names the limit the number comes from —
 * "Today it is the total…", or "On 22 Oct 2026 it was the total…".
 */
export function inoutAnswerWhyText(
  why: { defining: InOutLimitCounter; other: InOutLimitCounter },
  {
    t,
    formatNumber,
    country,
    pastDay,
  }: { t: TFunction; formatNumber: FormatNumber; country: string; pastDay: InOutPastDay }
): { title: string; body: string } | null {
  const defining = why.defining.figures;
  const other = why.other.figures;
  if (!defining || !other) return null;
  const heading = {
    count: defining.left,
    days: formatNumber(defining.left),
    other: formatNumber(other.left),
  };
  const byTotal = {
    count: defining.left,
    left: formatNumber(defining.left),
    limit: formatNumber(defining.limit),
  };
  const byInARow = { count: defining.limit, limit: formatNumber(defining.limit), country };
  const total = why.defining.clock.accrual === 'CUMULATIVE';
  if (pastDay === null) {
    return {
      title: t('inout.limits.why.title', heading),
      body: total
        ? t('inout.limits.why.byTotal', byTotal)
        : t('inout.limits.why.byInARow', byInARow),
    };
  }
  return {
    title: t('inout.limits.why.titleFrom', { ...heading, date: pastDay }),
    body: total
      ? t('inout.limits.why.byTotalOn', { ...byTotal, date: pastDay })
      : t('inout.limits.why.byInARowOn', { ...byInARow, date: pastDay }),
  };
}

/** A limit in a row's trip still under way — "This trip, since …", or the one under way on the day read. */
export function inoutOpenTripText(
  run: InOutLimitTrips['runs'][number],
  {
    t,
    formatNumber,
    from,
    pastDay,
  }: { t: TFunction; formatNumber: FormatNumber; from: string; pastDay: InOutPastDay }
): string {
  const said = { count: run.days, from, days: formatNumber(run.days) };
  return pastDay === null
    ? t('inout.limits.trips.thisTrip', said)
    : t('inout.limits.trips.thisTripOn', { ...said, date: pastDay });
}

/** How far the next trip can go, held by the other limit — from today, or from the day read. */
export function inoutNextTripText(
  trips: InOutLimitTrips & { next: NonNullable<InOutLimitTrips['next']> },
  { t, formatNumber, pastDay }: { t: TFunction; formatNumber: FormatNumber; pastDay: InOutPastDay }
): string {
  const said = {
    count: trips.next.days,
    days: formatNumber(trips.next.days),
    limit: formatNumber(trips.limit),
  };
  return pastDay === null
    ? t('inout.limits.trips.next', said)
    : t('inout.limits.trips.nextFrom', { ...said, date: pastDay });
}
